import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { JVInput } from "../../shared/JVInput";
import { JVDropdown } from "../../shared/JVDropdown";
import { JVCheckbox } from "../../shared/JVCheckbox";
import { JVMultiselect } from "../../shared/JVMultiselect";
import { JVDate } from "../../shared/JVDate";
import { TUserRecord, WorkTypeOptions } from "./modal";
import { TDropdown } from "../../shared/modal";
import { Fetch } from "../../utilities/fetch";
import { format } from "date-fns";
import { useToast } from "../../context/toastProvider";

const schema = yup.object({
    name: yup.string().required("Name is required*").min(3),
    email: yup.string().required("Email is required*").email(),
    password: yup.string().when("id", {
        is: (id: number) => !id,
        then: (schema) => schema.required("Password is required*").min(6).max(16)
    }),
    branchId: yup.number().min(1, "Branch is required*").required("Branch is required*"),
    status: yup.boolean().required("Status is required*"),
    workFrom: yup.string().required("WorkFrom is required*"),
    dateOfJoining: yup.date().optional(),
});

interface Iprops {
    data: TUserRecord,
    open: boolean,
    hideDialog: () => void,
    reload: () => void
}

export const Form = (props: Iprops) => {
    const { showToast } = useToast();
    const [branches, setBranches] = useState<TDropdown[]>();
    const [roles, setRoles] = useState<TDropdown[]>();
    const [selectedRoles, setSelectedRoles] = useState<TDropdown[]>();
    const [loading, setLoading] = useState(false);

    const { control, handleSubmit, formState: { errors }, reset } = useForm<TUserRecord>({
        resolver: yupResolver(schema),
        defaultValues: props.data,
        shouldFocusError: false,
    });

    useEffect(() => {
        loadBranches();
        loadRoles();
    }, []);

    useEffect(() => {
        reset(props.data);
        if (props.data.id) {
            loadAssignedRoles(props.data.id);
        } else {
            setSelectedRoles([]);
        }
    }, [reset, props.data]);

    const loadBranches = async () => {
        const data = await Fetch("Dropdown/branches");
        if (data) {
            setBranches(data);
        }
    }
    const loadRoles = async () => {
        const data = await Fetch("Dropdown/roles");
        if (data) {
            setRoles(data);
        }
    }
    const loadAssignedRoles = async (id: number) => {
        const data = await Fetch("Users/assignedRoles", { query: `userId=${id}` });
        if (data) {
            setSelectedRoles(data);
        }
    }

    const saveToDB = async (body: TUserRecord) => {
        if (loading) return;
        setLoading(true);

        const id = props.data.id;
        const method = id ? "PUT" : "POST";
        const message = id ? "Updated Successfully" : "Added Successfully";

        body.roles = selectedRoles?.map(r => r.id) ?? [];
        body.workFrom = Number(body.workFrom);
        body.dateOfJoining = format(new Date(body.dateOfJoining ?? ''), 'yyyy-MM-dd');

        const response = await Fetch(id ? `users/${id}` : 'users', {
            method, body,
            onError: (m) => showToast({ severity: 'error', detail: m })
        });

        if (response) {
            props.hideDialog();
            props.reload();
            showToast({ severity: 'success', detail: message });
        }

        setLoading(false);
    }

    const onHide = () => {
        props.hideDialog();
        setSelectedRoles(undefined);
    }

    function onSubmit(data: TUserRecord) {
        if (!selectedRoles?.length) {
            showToast({ severity: 'error', detail: "Please Select atleast one Role", life: 3000 });
            return;
        }
        saveToDB(data);
    }


    return (
        <Dialog visible={props.open} style={{ width: '80vw' }} header="Add/Edit User" modal className="p-fluid" onHide={onHide}>
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">
                <JVInput required label="Name" control={control} name="name" error={errors.name?.message} />
                <JVInput required label="Email" control={control} name="email" error={errors.email?.message} />
                <JVInput required={props.data.id ? false : true} label="Password" control={control} name="password" error={errors.password?.message} />
                <JVInput label="PhoneNo" control={control} name="phoneNo" error={errors.phoneNo?.message} />
                <JVDropdown required label="Branch" control={control} name="branchId" error={errors.branchId?.message} options={branches} optionLabel="name" optionValue="id" placeholder="Select Branch" />
                <JVDropdown required label="Work From" control={control} name="workFrom" error={errors.workFrom?.message}
                    options={WorkTypeOptions} optionLabel="name" optionValue="id" placeholder="Select Work From" />
                <JVDate label="Date Of Joining" control={control} name="dateOfJoining" error={errors.dateOfJoining?.message} />
                <JVMultiselect label="Assign Roles" options={roles} optionLabel="name" placeholder="Select Roles" required value={selectedRoles} onChange={setSelectedRoles} />
                <JVCheckbox column={4} required label="Status" control={control} name="status" />
                <div className="col-12 md:col-12">
                    <Button className="field col-12 md:col-6" loading={loading} label="SAVE" type="submit"></Button>
                    <Button className="field col-12 md:col-6 p-button-outlined" label="CLOSE" onClick={onHide} type="button"></Button>
                </div>
            </form>
        </Dialog>
    );
}